import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useConfigurationSelector } from "../redux/reducers/ConfigurationReducer/ConfigurationReducer";
import { useModalSelector } from "../redux/reducers/ModalReducer/ModalReducer";
import { usePremiumSelector } from "../redux/reducers/PremiumReducer/PremiumReducer";
import { useAppConfigurationSelector } from "../redux/reducers/AppConfigurationReducer/AppConfigurationReducer";
import { useMsTeamsSelector } from "../redux/reducers/MicrosoftTeamsReducer/MicrosoftTeamsReducer";
import { getBrowserName } from "../utils/getBrowserName";
import { AxiosConfig } from "../apis/AxiosConfig/AxiosConfig";
import { useSelector } from "react-redux";
import { useErrorHandling } from "./useErrorHandling/useErrorHandling";
import { IQueueType } from "../interfaces/IQueueType";
import { v4 as uuidv4 } from "uuid";
export const useTrack = () => {
    const location = useLocation();
    const { tryCatchAsync } = useErrorHandling();
    // Refs
    const values = useRef({
        browser: "",
    });
    const trackRef = useRef();
    const trackLeadRef = useRef();
    const config = useConfigurationSelector("data");
    const dialogs = useModalSelector("isOpen");
    const notifications = useSelector((s) => s.notifications);
    const premium = usePremiumSelector("adminCenterData", "isPremium", "isPlatinum", "subscriptionLicenceId", "isAdmin");
    const app = useAppConfigurationSelector("appOwner", "appId", "appName", "from");
    const teamsContext = useMsTeamsSelector("locale", "teamName", "channelName", "tenantId", "tabName", "userMail", "userId", "userName", "sku", "isInPersonalApp");
    useEffect(() => {
        values.current.browser = getBrowserName();
    }, []);
    useEffect(() => {
        buildParams();
    }, [config, dialogs, premium, app, teamsContext, location]);
    const buildParams = () => {
        const params = {
            id: uuidv4(),
            locale: teamsContext.locale,
            teamName: encodeURI(teamsContext.teamName),
            channelName: encodeURI(teamsContext.channelName),
            tenantId: teamsContext.tenantId,
            tabName: encodeURI(teamsContext.tabName),
            userMail: encodeURI(teamsContext.userMail),
            userId: teamsContext.userId,
            ...app,
            ...premium,
            isDiamond: false, // Should be updated when Diamond tier will be setup
            browser: encodeURI(values.current.browser),
            notificationsUpdate: !!notifications?.update,
            notificationsDelete: !!notifications?.delete,
            currentModal: encodeURI(dialogs.isOpen),
            appVersion: config.data.apiVersion,
            adminCenterAppId: config.data.adminCenterAppId,
            currentView: encodeURI(location.pathname),
        };
        const fullNameParts = teamsContext.userName.split(' ');
        const leadParams = {
            source: "teams",
            id: teamsContext.userId,
            email: encodeURI(teamsContext.userMail),
            firstName: encodeURI(fullNameParts[0]),
            lastName: encodeURI(fullNameParts.slice(1).join(' ')),
            company: undefined,
            country: undefined,
            jobTitle: undefined,
            phone: undefined,
            isSuperAdmin: premium.adminCenterData?.subscriptionOwner == teamsContext.userId,
            isAdmin: premium.isAdmin,
            subscriptions: undefined,
            app: {
                appName: app.from,
                downloadCount: undefined,
                lastDownloadDate: undefined,
                isPremium: premium.isPremium,
                isPlatinum: premium.isPlatinum,
                isDiamond: false, // Should be updated when Diamond tier will be setup
                cookiesExperience: undefined,
                cookiesOffer: undefined,
            }
        };
        trackRef.current = params;
        trackLeadRef.current = leadParams;
    };
    const errorTrack = (err, instance) => {
        if (!instance)
            return console.error("Not able to throw error on App Insight");
        const message = { ...trackRef.current, error: err };
        instance.trackException({ exception: { name: "Tracking Error", message } });
    };
    const trackClient = async (name, data, options) => {
        const trackedItems = { ...trackRef.current, name, data: JSON.stringify(data) };
        try {
            if (options.SQL.required) {
                AxiosConfig.updateHeadersToTrack(trackedItems, undefined);
                await options.SQL.apiCall();
            }
            if (options.appInsight.required) {
                options.appInsight.instance.trackEvent({ name, properties: trackedItems });
            }
        }
        catch (error) {
            errorTrack(error, options.appInsight.instance);
        }
    };
    // This function is called before axios call
    const trackServer = (name, data, queueType, extendedData) => {
        let trackedItems = { ...trackRef.current, name, data: JSON.stringify(data) };
        if (queueType === IQueueType.TrackLead) {
            trackLeadRef.current.app.downloadCount = extendedData.downloadCount;
            trackLeadRef.current.app.lastDownloadDate = extendedData.lastDownloadDate;
            trackLeadRef.current.app.cookiesExperience = extendedData.cookies?.experience;
            trackLeadRef.current.app.cookiesOffer = extendedData.cookies?.offer;
            trackedItems = trackLeadRef.current;
        }
        AxiosConfig.updateHeadersToTrack(trackedItems, queueType);
    };
    /**
     * @deprecated since version 1.0
     */
    function trackItAsync(name, callback, queueType) {
        return async (...args) => {
            try {
                trackServer(name, args, queueType);
                const { state, error, result } = await tryCatchAsync(callback)(...args);
                if (!state)
                    throw error;
                return result;
            }
            catch (error) {
                throw error;
            }
        };
    }
    function trackItAsync2(name, callback, queueType, extendedData) {
        return async (...args) => {
            try {
                trackServer(name, args, queueType, extendedData);
                const res = await tryCatchAsync(callback)(...args);
                if (res.error || !res.state)
                    throw res.error;
                return res;
            }
            catch (error) {
                return { state: false, error, variables: args, result: null };
            }
        };
    }
    return { trackClient, trackServer, trackItAsync, trackItAsync2 };
};
